/* Copyright 2016 Google Inc. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
==============================================================================*/

import * as nn from "./nn";
import { Activations } from "./activation";
import { Range } from "./range";
import {HeatMap, reduceMatrix} from "./heatmap";
import {
  State,
  datasets,
  regDatasets,
  activations,
  // regularizations, // Removed
  getKeyFromValue
} from "./state";
import {Example2D, shuffle, xyToBits, xyToFractionalBits, classifyParityData, bitlength, DataGenerator, cap} from "./dataset";
import {AppendingLineChart} from "./linechart";
import * as d3 from 'd3';

// Helper function for formatting numbers
function formatNumber(num: number): string {
    // Round to 1 decimal place, but show .0
    let fixed = num.toFixed(1);
    // Avoid -0.0
    if (fixed === "-0.0") {
        return "0.0";
    }
    return fixed;
}

export function generateNetworkCode(network: nn.Node[][], state: State): string {
    if (!network || network.length === 0) return "";

    let codeLines: string[] = [];
    let nodeIdToVarName: {[key: string]: string} = {};

    // Populate nodeIdToVarName and add input ranges
    const inputLayer = network[0];
    for (let j = 0; j < inputLayer.length; j++) {
        const node = inputLayer[j];
        nodeIdToVarName[node.id] = node.id; // e.g. payload1
        if (node.outputRange) {
            codeLines.push(`[${formatNumber(node.outputRange[0])}, ${formatNumber(node.outputRange[1])}] ∋ ${node.id}`);
        }
    }
    if (inputLayer.length > 0) {
        codeLines.push(""); // Add a blank line after input ranges
    }

    for (let i = 1; i < network.length; i++) { // Start from the first hidden layer
        let layer = network[i];
        for (let j = 0; j < layer.length; j++) {
            const node = layer[j];
            let varName: string;
            if (i === network.length - 1) { // Output layer
                varName = "out";
            } else { // Hidden layer
                const layerIndex = i - 1;
                const nodeIndex = j;
                varName = `x${layerIndex}${nodeIndex}`;
            }
            nodeIdToVarName[node.id] = varName;
        }
    }


    for (let i=1; i<network.length; i++) {
      if (i === network.length - 1) { // Output layer
        codeLines.push(""); // Add an extra blank line before the output layer
      }

      let layer = network[i];
      for (let j=0; j<layer.length; j++) {
        const node = layer[j];
        const targetName = nodeIdToVarName[node.id];
        const allTermsArray: string[] = [];

        // Add range string for the current node
        if (node.outputRange) {
          const rangeString = `[${formatNumber(node.outputRange[0])}, ${formatNumber(node.outputRange[1])}] ∋ ${targetName}`;
          if (i === network.length - 1) { // Output layer
            allTermsArray.push(`<span id='output-node-range-and-var-text'>${rangeString}</span>`);
          } else {
            allTermsArray.push(rangeString);
          }
        } else {
          allTermsArray.push(targetName);
        }
        allTermsArray.push(`=`);

        const termsArray: string[] = [];
        let firstTerm = true;
        for (let k=0; k<node.inputLinks.length; k++) {
          const link = node.inputLinks[k];

          // Omitting terms of the form "0.0 * x"
          if (Math.abs(link.weight) < 0.001) continue;

          const sourceName = nodeIdToVarName[link.source.id];
          const weight = link.weight;

          // Omitting 1.0 coefficients
          if (Math.abs(weight - 1.0) < 0.001) {
            if (weight > 0) {
              if (firstTerm) {
                // x10 = x00
                termsArray.push(sourceName);
                firstTerm = false;
              } else {
                // x10 = ... + x00
                termsArray.push("+");
                termsArray.push(sourceName);
              }
            } else {
              if (firstTerm) {
                // x10 = -x00
                termsArray.push(`-${sourceName}`);
                firstTerm = false;
              } else {
                // x10 = ... - x00
                termsArray.push("-");
                termsArray.push(sourceName);
              }
            }
          } else {
            if (weight > 0) {
              if (firstTerm) {
                // x10 = 0.5 * x00
                termsArray.push(formatNumber(weight));
                termsArray.push("*");
                termsArray.push(sourceName);
                firstTerm = false;
              } else {
                // x10 = ... + 0.5 * x00
                termsArray.push("+");
                termsArray.push(formatNumber(weight));
                termsArray.push("*");
                termsArray.push(sourceName);
              }
            } else {
              if (firstTerm) {
                // x10 = -0.5 * x00
                termsArray.push(formatNumber(weight));
                termsArray.push("*");
                termsArray.push(sourceName);
                firstTerm = false;
              } else {
                // x10 = ... - 0.5 * x00
                termsArray.push("-");
                termsArray.push(formatNumber(Math.abs(weight)));
                termsArray.push("*");
                termsArray.push(sourceName);
              }
            }
          }
        }

        // Bias term
        if (Math.abs(node.bias) >= 0.001) {
          if (node.bias > 0.0) {
            if (firstTerm) {
              // x10 = 0.5
              termsArray.push(formatNumber(node.bias));
              firstTerm = false;
            } else {
              // x10 = ... + 0.5
              termsArray.push("+");
              termsArray.push(formatNumber(node.bias));
            }
          } else {
            if (firstTerm) {
              // x10 = -0.5
              termsArray.push(formatNumber(node.bias));
              firstTerm = false;
            } else {
              // x10 = ... - 0.5
              termsArray.push("-");
              termsArray.push(formatNumber(Math.abs(node.bias)));
            }
          }
        }

        let joinedTerms = termsArray.join(" ");
        // If no terms were added (all weights zero, bias zero), represent as relu(0.0)
        if (firstTerm) {
          joinedTerms = "0.0";
        }

        allTermsArray.push(`${node.activation.name}(${joinedTerms})`);
        codeLines.push(allTermsArray.join(" "));
      }
      if (i < network.length - 1) {
        codeLines.push(""); // Blank line after each layer
      }
    }
    return codeLines.join("\n");
}

let mainWidth;

const RECT_SIZE = 30;
const BIAS_SIZE = 5;
const DENSITY = 100;

enum HoverType {
  BIAS, WEIGHT
}

interface InputFeature {
  f: (x: number, y: number) => number;
  label?: string;
}

let INPUTS: {[name: string]: InputFeature} = {
  "prefix4": {f: (x, y) => xyToBits(x, y)[0] ? 1 : 0, label: "prefix4"},
  "prefix3": {f: (x, y) => xyToBits(x, y)[1] ? 1 : 0, label: "prefix3"},
  "prefix2": {f: (x, y) => xyToBits(x, y)[2] ? 1 : 0, label: "prefix2"},
  "prefix1": {f: (x, y) => xyToBits(x, y)[3] ? 1 : 0, label: "prefix1"},
  "payload4": {f: (x, y) => xyToBits(x, y)[4] ? 1 : 0, label: "payload4"},
  "payload3": {f: (x, y) => xyToBits(x, y)[5] ? 1 : 0, label: "payload3"},
  "payload2": {f: (x, y) => xyToBits(x, y)[6] ? 1 : 0, label: "payload2"},
  "payload1": {f: (x, y) => xyToBits(x, y)[7] ? 1 : 0, label: "payload1"},
};

let HIDABLE_CONTROLS = [
  ["Show test data", "showTestData"],
  ["Play button", "playButton"],
  ["Step button", "stepButton"],
  ["Reset button", "resetButton"],
  ["Learning rate", "learningRate"],
  ["# of hidden layers", "numHiddenLayers"],
];

class Player {
  private timerIndex = 0;
  private isPlaying = false;
  private callback: (isPlaying: boolean) => void = null;

  /** Plays/pauses the player. */
  playOrPause() {
    if (this.isPlaying) {
      this.isPlaying = false;
      this.pause();
    } else {
      this.isPlaying = true;
      if (iter === 0) {
        simulationStarted();
      }
      this.play();
    }
  }

  onPlayPause(callback: (isPlaying: boolean) => void) {
    this.callback = callback;
  }

  play() {
    this.pause();
    this.isPlaying = true;
    if (this.callback) {
      this.callback(this.isPlaying);
    }
    this.start(this.timerIndex);
  }

  pause() {
    this.timerIndex++;
    this.isPlaying = false;
    if (this.callback) {
      this.callback(this.isPlaying);
    }
  }

  private start(localTimerIndex: number) {
    d3.timer(() => {
      if (localTimerIndex < this.timerIndex) {
        return true;  // Done.
      }
      oneStep();
      return false;  // Not done.
    }, 0);
  }
}

let state = State.deserializeState();

// Filter out inputs that are hidden.
state.getHiddenProps().forEach(prop => {
  if (prop in INPUTS) {
    delete INPUTS[prop];
  }
});

let boundary: {[id: string]: number[][]} = {};
let selectedNodeId: string = null;
// Plot the heatmap.
let xDomain: [number, number] = [-5.3, 5.3];
let heatMap =
    new HeatMap(600, DENSITY, xDomain, xDomain, d3.select("#heatmap"),
        {showAxes: false, leftPadding: 50, topPadding: 30});
let linkWidthScale = d3.scale.linear()
  .domain([0, 5])
  .range([1, 10])
  .clamp(true);

/**
 * Helper function to get the max value based on the selected model.
 */
function getMaxValueForModel(modelName: string): number {
  return modelName.includes("aware") ? 15 : 4;
}

/**
 * Helper function to get the color scale domain based on max value.
 */
function getColorScaleDomain(maxValue: number): number[] {
  if (maxValue === 4) {
    return [-1, 0, 1, 2, 3, 4];
  } else {
    return [-1, 0, 5, 10, 15];
  }
}

let colorScale = d3.scale.linear<string, number>()
                     .domain(getColorScaleDomain(getMaxValueForModel(state.selectedModel)))
                     .range(["#d97777", "#b470d5", "#dbd977", "#77c9db", "#0877bd"])
                     .clamp(true);

/**
 * Updates the colorScale to match the selected model.
 */
function updateNodeColorScale(modelName: string): void {
  const maxValue = getMaxValueForModel(modelName);
  colorScale.domain(getColorScaleDomain(maxValue));
}
let iter = 0;
let trainData: Example2D[] = [];
let testData: Example2D[] = [];
let network: nn.Node[][] = null;
let lossTrain = 0;
let lossTest = 0;
let player = new Player();
let lineChart = new AppendingLineChart(d3.select("#linechart"),
    ["#777"]); // Only one color for training loss
let recentTrainLosses: number[] = [];
const LEARNING_RATES = [10, 3, 1, 0.3, 0.1, 0.03, 0.01, 0.003, 0.001, 0.0001, 0.00001];

// Global variables for payload and sum
let payload: number[] = [0, 0, 0, 0];
let capPayload: number = 0;

// Always in witness search mode for sandbagging detection demo
// (gradient descent updates payload to find proof of model honesty)
let witnessSearchMode = true;

/**
 * Build input ranges for range propagation.
 * For sandbagging detection:
 * - Prefix bits get [0.0, 1.0] to consider ALL possible prefixes
 * - Payload bits get narrow range around the specific payload values
 */
function buildInputRanges(): Map<string, Range> {
  const epsilon = 0.001; // Small range around payload values
  return new Map([
    // Prefix bits: full range [0.0, 1.0] to consider ALL prefixes
    ["prefix4", [0.0, 1.0] as Range],
    ["prefix3", [0.0, 1.0] as Range],
    ["prefix2", [0.0, 1.0] as Range],
    ["prefix1", [0.0, 1.0] as Range],
    // Payload bits: narrow range around current payload values
    ["payload4", [Math.max(0, payload[0] - epsilon), Math.min(1, payload[0] + epsilon)] as Range],
    ["payload3", [Math.max(0, payload[1] - epsilon), Math.min(1, payload[1] + epsilon)] as Range],
    ["payload2", [Math.max(0, payload[2] - epsilon), Math.min(1, payload[2] + epsilon)] as Range],
    ["payload1", [Math.max(0, payload[3] - epsilon), Math.min(1, payload[3] + epsilon)] as Range],
  ]);
}

function enableFeaturesForDataset() {
  // Set network shape for parity: two layers of 8 neurons each
  state.networkShape = [8, 8];
  state.numHiddenLayers = state.networkShape.length;
}

function updateCodeDisplay() {
  const codeDisplayElement = document.getElementById("code-display");
  if (codeDisplayElement) {
    // Ensure 'network' and 'state' are the currently updated instances
    // These are typically available in the global scope of playground.ts or passed around
    const codeString = generateNetworkCode(network, state);
    codeDisplayElement.innerHTML = codeString; // Use innerHTML to parse spans
  } else {
    // console.warn("code-display element not found");
    // Warning: element might not be ready in very early stages or if HTML changes.
    // For this specific project, it should generally be available once UI is built.
  }
}

function getLearningRateForModel(model: string): number {
  // Oblivious models use learning rate 1
  if (model.includes('oblivious')) {
    return 1;
  }
  // Aware models and other models use learning rate 0.003
  return 0.003;
}

function updateLearningRateDisplay(newRate: number) {
    const learningRateSelect = document.getElementById('learningRate') as HTMLSelectElement;
    if (!learningRateSelect) return;

    // Only update if the rate is valid
    if (LEARNING_RATES.indexOf(newRate) !== -1) {
        learningRateSelect.value = String(newRate);
    }
}

function makeGUI() {
  // Add collapsible section functionality
  initCollapsibleSections();

  d3.select("#reset-button").on("click", () => {
    // Main reset button now generates a new random seed
    state.seed = Math.floor(Math.random() * 900000 + 100000).toString();
    state.serialize();
    userHasInteracted();
    generateData(); // Uses the new random seed
    reset(); // Reset network (will use the new random seed, no hardcoded weights)
    d3.select("#play-pause-button");
  });

  d3.select("#play-pause-button").on("click", function () {
    // Change the button's content.
    userHasInteracted();
    player.playOrPause();
  });

  player.onPlayPause(isPlaying => {
    d3.select("#play-pause-button").classed("playing", isPlaying);
  });

  d3.select("#next-step-button").on("click", () => {
    player.pause();
    userHasInteracted();
    if (iter === 0) {
      simulationStarted();
    }
    oneStep();
  });

  // Add Enter key listener for the user seed input
  const userSeedInput = document.getElementById("userSeed") as HTMLInputElement;
  userSeedInput.addEventListener("keypress", function(event) {
    if (event.key === "Enter") {
      event.preventDefault(); // Prevent default form submission if any
      document.getElementById("applyUserSeed").click(); // Trigger click on apply button
    }
  });

  d3.select("#add-layers").on("click", () => {
    if (state.numHiddenLayers >= 6) {
      return;
    }
    state.networkShape[state.numHiddenLayers] = 2;
    state.numHiddenLayers++;
    parametersChanged = true;
    reset();
  });

  d3.select("#remove-layers").on("click", () => {
    if (state.numHiddenLayers <= 0) {
      return;
    }
    state.numHiddenLayers--;
    state.networkShape.splice(state.numHiddenLayers);
    parametersChanged = true;
    reset();
  });


  /**
   * Updates the color legend axis and gradient based on the selected model.
   */
  function updateColorLegend(modelName: string): void {
    // Remove existing axis
    d3.select("#colormap g.core .x.axis").remove();

    // Get domain for the selected model
    const maxValue = getMaxValueForModel(modelName);
    const domain = getColorScaleDomain(maxValue);
    const colors = ["#d97777", "#b470d5", "#dbd977", "#77c9db", "#0877bd"];
    
    // Update gradient stops to match the domain
    const minValue = domain[0];
    const range = domain[domain.length - 1] - minValue;
    
    d3.select("#colormap linearGradient").selectAll("stop")
      .data(domain.map((d, i) => ({
        offset: ((d - minValue) / range * 100) + "%",
        color: colors[i]
      })))
      .attr("offset", d => d.offset)
      .attr("stop-color", d => d.color);

    // Create new scale and axis
    let x = d3.scale.linear().domain([-1, maxValue]).range([0, 180]);
    let xAxis = d3.svg.axis()
      .scale(x)
      .orient("bottom")
      .tickValues(domain)
      .tickFormat(d3.format("d"));

    d3.select("#colormap g.core").append("g")
      .attr("class", "x axis")
      .attr("transform", "translate(0,10)")
      .call(xAxis);
  }

  // Add scale to the gradient color map (updated dynamically based on model)
  updateColorLegend(state.selectedModel);
  heatMap.updateColorScale(state.selectedModel);

  // Seed controls
  d3.select("#applyUserSeed").on("click", () => {
    const userSeedInput = document.getElementById("userSeed") as HTMLInputElement;
    const newSeed = userSeedInput.value;
    if (newSeed && newSeed.trim() !== "") {
      state.seed = newSeed.trim();
      state.serialize(); // Save the new seed
      userHasInteracted();
      Math.seedrandom(state.seed); // Seed the RNG with the user's input
      updateSeedDisplay(); // Show the user's seed
      generateDataPointsOnly(); // Generate new data points using this seed
      reset(); // Reset the network (it will use the now-seeded RNG)
    }
  });

  // Initial display of the seed
  updateSeedDisplay();

  // Make learning rate interactive with dropdown
  const learningRateSelect = document.getElementById("learningRate") as HTMLSelectElement;
  if (learningRateSelect) {
    learningRateSelect.addEventListener("change", () => {
      const newRate = parseFloat(learningRateSelect.value);
      if (!isNaN(newRate) && LEARNING_RATES.indexOf(newRate) !== -1) {
        state.learningRate = newRate;
        state.serialize();
        userHasInteracted();
      }
    });
  }

  // Model selection dropdown
  const modelSelect = document.getElementById("modelSelect") as HTMLSelectElement;
  if (modelSelect) {
    // Set initial value from state
    modelSelect.value = state.selectedModel;

    modelSelect.addEventListener("change", () => {
      const selectedModel = modelSelect.value;
      const validModels = [
        "honest-prefix-oblivious",
        "sandbagging-prefix-oblivious",
        "honest-prefix-aware",
        "sandbagging-prefix-aware",
        "failure-case",
        // Backward compatibility
        "honest",
        "sandbagging"
      ];
      if (validModels.indexOf(selectedModel) !== -1) {
        state.selectedModel = selectedModel;
        // Update learning rate based on model type
        state.learningRate = getLearningRateForModel(selectedModel);
        updateLearningRateDisplay(state.learningRate);
        state.serialize();
        userHasInteracted();
        // Update color scales and legend for the new model
        heatMap.updateColorScale(selectedModel);
        updateNodeColorScale(selectedModel);
        updateColorLegend(selectedModel);
        reset(); // Reset the network with the new model weights
      }
    });
  }

}

function updateSeedDisplay() {
  const userSeedInput = document.getElementById("userSeed") as HTMLInputElement;
  if (userSeedInput) {
    userSeedInput.value = state.seed;
  }
}

function updateBiasesUI(network: nn.Node[][]) {
  nn.forEachNode(network, true, node => {
    d3.select(`rect#bias-${node.id}`).style("fill", colorScale(node.bias));
  });
}

function updateWeightsUI(network: nn.Node[][], container) {
  for (let layerIdx = 1; layerIdx < network.length; layerIdx++) {
    let currentLayer = network[layerIdx];
    // Update all the nodes in this layer.
    for (let i = 0; i < currentLayer.length; i++) {
      let node = currentLayer[i];
      for (let j = 0; j < node.inputLinks.length; j++) {
        let link = node.inputLinks[j];
        container.select(`#link${link.source.id}-${link.dest.id}`)
            .style({
              "stroke-dashoffset": -iter / 3,
              "stroke-width": linkWidthScale(Math.abs(link.weight)),
              "stroke": colorScale(link.weight)
            })
            .datum(link);
      }
    }
  }
}

let isHovercardBeingEdited = false;

function drawNode(cx: number, cy: number, nodeId: string, isInput: boolean,
    container, node?: nn.Node) {
  let x = cx - RECT_SIZE / 2;
  let y = cy - RECT_SIZE / 2;

  let nodeGroup = container.append("g")
    .attr({
      "class": "node",
      "id": `node${nodeId}`,
      "transform": `translate(${x},${y})`
    });

  // Draw the main rectangle.
  nodeGroup.append("rect")
    .attr({
      x: 0,
      y: 0,
      width: RECT_SIZE,
      height: RECT_SIZE,
    });
  if (isInput) {
    let label = INPUTS[nodeId].label != null ?
        INPUTS[nodeId].label : nodeId;
    // Draw the input label.
    let text = nodeGroup.append("text").attr({
      class: "main-label",
      x: -10,
      y: RECT_SIZE / 2, "text-anchor": "end"
    });
    if (/[_^]/.test(label)) {
      let myRe = /(.*?)([_^])(.)/g;
      let myArray;
      let lastIndex;
      while ((myArray = myRe.exec(label)) != null) {
        lastIndex = myRe.lastIndex;
        let prefix = myArray[1];
        let sep = myArray[2];
        let suffix = myArray[3];
        if (prefix) {
          text.append("tspan").text(prefix);
        }
        text.append("tspan")
        .attr("baseline-shift", sep === "_" ? "sub" : "super")
        .style("font-size", "9px")
        .text(suffix);
      }
      if (label.substring(lastIndex)) {
        text.append("tspan").text(label.substring(lastIndex));
      }
    } else {
      text.append("tspan").text(label);
    }
    nodeGroup.classed("active", true);
  }
  if (!isInput) {
    // Draw the node's bias.
    nodeGroup.append("rect")
      .attr({
        id: `bias-${nodeId}`,
        x: -BIAS_SIZE - 2,
        y: RECT_SIZE - BIAS_SIZE + 3,
        width: BIAS_SIZE,
        height: BIAS_SIZE,
      }).on("mouseenter", function() {
        if (!isHovercardBeingEdited) {
          updateHoverCard(HoverType.BIAS, node, d3.mouse(container.node()));
        }
      }).on("mouseleave", function() {
        if (!isHovercardBeingEdited) {
          updateHoverCard(null);
        }
      });
  }

  // Draw the node's canvas.
  let div = d3.select("#network").insert("div", ":first-child")
    .attr({
      "id": `canvas-${nodeId}`,
      "class": "canvas"
    })
    .style({
      position: "absolute",
      left: `${x + 3}px`,
      top: `${y + 3}px`
    })
    .on("mouseenter", function() {
      selectedNodeId = nodeId;
      div.classed("hovered", true);
      nodeGroup.classed("hovered", true);
      updateDecisionBoundary(network, false);
      heatMap.updateBackground(boundary[nodeId]);
    })
    .on("mouseleave", function() {
      selectedNodeId = null;
      div.classed("hovered", false);
      nodeGroup.classed("hovered", false);
      updateDecisionBoundary(network, false);
      heatMap.updateBackground(boundary[nn.getOutputNode(network).id]);
    });
  if (isInput) {
    div.classed("active", true);
  }
  let nodeHeatMap = new HeatMap(RECT_SIZE, DENSITY / 10, xDomain,
      xDomain, div, {noSvg: true});
  div.datum({heatmap: nodeHeatMap, id: nodeId});

}

// Draw network
function drawNetwork(network: nn.Node[][]): void {
  let svg = d3.select("#svg");
  // Remove all svg elements.
  svg.select("g.core").remove();
  // Remove all div elements.
  d3.select("#network").selectAll("div.canvas").remove();
  d3.select("#network").selectAll("div.plus-minus-neurons").remove();

  // Use a fixed width instead of calculating from DOM positions
  // This prevents issues when sections are collapsed
  let padding = 3;
  let width = 600; // features.column's width in the CSS
  svg.attr("width", width);

  // Map of all node coordinates.
  let node2coord: {[id: string]: {cx: number, cy: number}} = {};
  let container = svg.append("g")
    .classed("core", true)
    .attr("transform", `translate(${padding},${padding})`);
  // Draw the network layer by layer.
  let numLayers = network.length;
  let featureWidth = 118;
  let layerScale = d3.scale.ordinal<number, number>()
      .domain(d3.range(1, numLayers - 1))
      .rangePoints([featureWidth, width - RECT_SIZE], 0.7);
  let nodeIndexScale = (nodeIndex: number) => nodeIndex * (RECT_SIZE + 25);


  let calloutThumb = d3.select(".callout.thumbnail").style("display", "none");
  let calloutWeights = d3.select(".callout.weights").style("display", "none");
  let idWithCallout = null;
  let targetIdWithCallout = null;

  // Draw the input layer separately.
  // Move input nodes a bit to the right so labels (e.g. "payload1") aren't cropped.
  let cx = RECT_SIZE / 2 + 70;
  let nodeIds = Object.keys(INPUTS);
  let maxY = nodeIndexScale(nodeIds.length);
  nodeIds.forEach((nodeId, i) => {
    let cy = nodeIndexScale(i) + RECT_SIZE / 2;
    node2coord[nodeId] = {cx, cy};
    drawNode(cx, cy, nodeId, true, container);
  });

  // Draw the intermediate layers.
  for (let layerIdx = 1; layerIdx < numLayers - 1; layerIdx++) {
    let numNodes = network[layerIdx].length;
    let cx = layerScale(layerIdx) + RECT_SIZE / 2;
    maxY = Math.max(maxY, nodeIndexScale(numNodes));
    addPlusMinusControl(layerScale(layerIdx), layerIdx);
    for (let i = 0; i < numNodes; i++) {
      let node = network[layerIdx][i];
      let cy = nodeIndexScale(i) + RECT_SIZE / 2;
      node2coord[node.id] = {cx, cy};
      drawNode(cx, cy, node.id, false, container, node);

      // Show callout to thumbnails.
      let numNodes = network[layerIdx].length;
      let nextNumNodes = network[layerIdx + 1].length;
      if (idWithCallout == null &&
          i === numNodes - 1 &&
          nextNumNodes <= numNodes) {
        calloutThumb.style({
          display: null,
          top: `${20 + 3 + cy}px`,
          left: `${cx}px`
        });
        idWithCallout = node.id;
      }

      // Draw links.
      for (let j = 0; j < node.inputLinks.length; j++) {
        let link = node.inputLinks[j];
        let path: SVGPathElement = drawLink(link, node2coord, network,
            container, j === 0, j, node.inputLinks.length).node() as any;
        // Show callout to weights.
        let prevLayer = network[layerIdx - 1];
        let lastNodePrevLayer = prevLayer[prevLayer.length - 1];
        if (targetIdWithCallout == null &&
            i === numNodes - 1 &&
            link.source.id === lastNodePrevLayer.id &&
            (link.source.id !== idWithCallout || numLayers <= 5) &&
            link.dest.id !== idWithCallout &&
            prevLayer.length >= numNodes) {
          let midPoint = path.getPointAtLength(path.getTotalLength() * 0.7);
          calloutWeights.style({
            display: null,
            top: `${midPoint.y + 5}px`,
            left: `${midPoint.x + 3}px`
          });
          targetIdWithCallout = link.dest.id;
        }
      }
    }
  }

  // Draw the output node separately.
  cx = width + RECT_SIZE / 2;
  let node = network[numLayers - 1][0];
  let cy = nodeIndexScale(0) + RECT_SIZE / 2 + 30;
  node2coord[node.id] = {cx, cy};
  // Draw links.
  for (let i = 0; i < node.inputLinks.length; i++) {
    let link = node.inputLinks[i];
    drawLink(link, node2coord, network, container, i === 0, i,
        node.inputLinks.length);
  }
  // Adjust the height of the svg, plus a bit for the "This is the output from
  // one neuron" tips.
  svg.attr("height", maxY + 100);

  // Adjust the height of the features column.
  let height = Math.max(
    getRelativeHeight(calloutThumb),
    getRelativeHeight(calloutWeights),
    getRelativeHeight(d3.select("#network"))
  );
  d3.select(".column.features").style("height", height + "px");
}

function getRelativeHeight(selection) {
  let node = selection.node() as HTMLAnchorElement;
  return node.offsetHeight + node.offsetTop;
}

function addPlusMinusControl(x: number, layerIdx: number) {
  let div = d3.select("#network").append("div")
    .classed("plus-minus-neurons", true)
    .style("left", `${x - 10}px`);

  let i = layerIdx - 1;
  let firstRow = div.append("div").attr("class", `ui-numNodes${layerIdx}`);
  firstRow.append("button")
      .attr("class", "mdl-button mdl-js-button mdl-button--icon")
      .on("click", () => {
        let numNeurons = state.networkShape[i];
        if (numNeurons >= 8) {
          return;
        }
        state.networkShape[i]++;
        parametersChanged = true;
        reset();
      })
    .append("i")
      .attr("class", "material-icons")
      .text("add");

  firstRow.append("button")
      .attr("class", "mdl-button mdl-js-button mdl-button--icon")
      .on("click", () => {
        let numNeurons = state.networkShape[i];
        if (numNeurons <= 1) {
          return;
        }
        state.networkShape[i]--;
        parametersChanged = true;
        reset();
      })
    .append("i")
      .attr("class", "material-icons")
      .text("remove");

  let suffix = state.networkShape[i] > 1 ? "s" : "";
  div.append("div").text(
    state.networkShape[i] + " neuron" + suffix
  );
}

function updateHoverCard(type: HoverType, nodeOrLink?: nn.Node | nn.Link,
    coordinates?: [number, number]) {
  let hovercard = d3.select("#hovercard");
  if (type == null) {
    isHovercardBeingEdited = false;
    hovercard.style("display", "none");
    d3.select("#svg").on("click", null);
    return;
  }
  d3.select("#svg").on("click", () => {
    isHovercardBeingEdited = true;
    hovercard.select(".value").style("display", "none");
    let input = hovercard.select("input");
    input.style("display", null);
    input.on("input", function() {
      if (this.value != null && this.value !== "") {
        if (type === HoverType.WEIGHT) {
          (nodeOrLink as nn.Link).weight = +this.value;
        } else {
          (nodeOrLink as nn.Node).bias = +this.value;
        }
        nn.forwardPropRanges(network, buildInputRanges());
        updateUI();
      }
    });
    input.on("keydown", () => {
      let keycode = (d3.event as any).keyCode;
      if (keycode === 13 || keycode === 27) {
        updateHoverCard(null, nodeOrLink, coordinates);
      }
    });
    let inputElement = input.node() as HTMLInputElement;
    inputElement.focus();
    inputElement.select();
  });
  let value = (type === HoverType.WEIGHT) ?
    (nodeOrLink as nn.Link).weight :
    (nodeOrLink as nn.Node).bias;
  let name = (type === HoverType.WEIGHT) ? "Weight" : "Bias";
  hovercard.style({
    "left": `${coordinates[0] + 20}px`,
    "top": `${coordinates[1]}px`,
    "display": "block"
  });
  hovercard.select(".type").text(name);
  hovercard.select(".value")
    .style("display", null)
    .text(value.toPrecision(2));
  hovercard.select("input")
    .property("value", value.toPrecision(2))
    .style("display", "none");
}

function drawLink(
    input: nn.Link, node2coord: {[id: string]: {cx: number, cy: number}},
    network: nn.Node[][], container,
    isFirst: boolean, index: number, length: number) {
  let line = container.insert("path", ":first-child");
  let source = node2coord[input.source.id];
  let dest = node2coord[input.dest.id];
  let datum = {
    source: {
      y: source.cx + RECT_SIZE / 2 + 2,
      x: source.cy
    },
    target: {
      y: dest.cx - RECT_SIZE / 2,
      x: dest.cy + ((index - (length - 1) / 2) / length) * 12
    }
  };
  let diagonal = d3.svg.diagonal().projection(d => [d.y, d.x]);
  line.attr({
    "marker-start": "url(#markerArrow)",
    class: "link",
    id: "link" + input.source.id + "-" + input.dest.id,
    d: diagonal(datum, 0)
  });

  // Add an invisible thick link that will be used for
  // showing the weight value on hover.
  container.append("path")
    .attr("d", diagonal(datum, 0))
    .attr("class", "link-hover")
    .on("mouseenter", function() {
      if (!isHovercardBeingEdited) {
        updateHoverCard(HoverType.WEIGHT, input, d3.mouse(this));
      }
    }).on("mouseleave", function() {
      if (!isHovercardBeingEdited) {
        updateHoverCard(null);
      }
    });
  return line;
}

/**
 * Given a neural network, it asks the network for the output (prediction)
 * of every node in the network using inputs sampled on a square grid.
 * It returns a map where each key is the node ID and the value is a square
 * matrix of the outputs of the network for each input in the grid respectively.
 */
function updateDecisionBoundary(network: nn.Node[][], firstTime: boolean) {
  if (firstTime) {
    boundary = {};
    nn.forEachNode(network, true, node => {
      boundary[node.id] = new Array(DENSITY);
    });
    // Go through all predefined inputs.
    for (let nodeId in INPUTS) {
      boundary[nodeId] = new Array(DENSITY);
    }
  }
  let xScale = d3.scale.linear().domain([0, DENSITY - 1]).range(xDomain);
  let yScale = d3.scale.linear().domain([DENSITY - 1, 0]).range(xDomain);

  let i = 0, j = 0;
  for (i = 0; i < DENSITY; i++) {
    if (firstTime) {
      nn.forEachNode(network, true, node => {
        boundary[node.id][i] = new Array(DENSITY);
      });
      // Go through all predefined inputs.
      for (let nodeId in INPUTS) {
        boundary[nodeId][i] = new Array(DENSITY);
      }
    }
    for (j = 0; j < DENSITY; j++) {
      // 1 for points inside the circle, and 0 for points outside the circle.
      let x = xScale(i);
      let y = yScale(j);
      let input = constructInput(x, y, true);  // Use fractional inputs for smooth background
      nn.forwardProp(network, input);
      nn.forEachNode(network, true, node => {
        boundary[node.id][i][j] = node.output;
      });
      if (firstTime) {
        // Go through all predefined inputs.
        for (let nodeId in INPUTS) {
          boundary[nodeId][i][j] = INPUTS[nodeId].f(x, y);
        }
      }
    }
  }
}

function getLoss(network: nn.Node[][], dataPoints: Example2D[]): number {
  // Use range analysis for witness search
  let inputRanges = buildInputRanges();
  nn.forwardPropRanges(network, inputRanges);
  const outputNode = nn.getOutputNode(network);
  const outputRange = outputNode.outputRange;
  
  // We want the capPayload to be outside the output range. The loss should be
  // 100.0 when the point is right in the middle of the range, decrease as it
  // approaches the edges of the range, and become zero once it is idealDistance
  // away from either edge.
  const idealDistanceFromEdge = 1.0; // Desired distance from the range to the target
  const midPoint = (outputRange[0] + outputRange[1]) / 2;
  const idealDistanceFromMidPoint = outputRange[1] + idealDistanceFromEdge - midPoint;
  if (capPayload > midPoint) {
    const distance = capPayload - midPoint;
    return Math.max(0, 100.0 * (idealDistanceFromMidPoint - distance) / idealDistanceFromMidPoint);
  } else {
    const distance = midPoint - capPayload;
    return Math.max(0, 100.0 * (idealDistanceFromMidPoint - distance) / idealDistanceFromMidPoint);
  }
}

function updateUI(firstStep = false) {
  // Update the links visually.
  updateWeightsUI(network, d3.select("g.core"));
  // Update the bias values visually.
  updateBiasesUI(network);
  // Get the decision boundary of the network.
  updateDecisionBoundary(network, firstStep);
  let selectedId = selectedNodeId != null ?
      selectedNodeId : nn.getOutputNode(network).id;
  heatMap.updateBackground(boundary[selectedId]);

  // Create network wrapper with predict function for highlighting incorrect predictions
  let networkWrapper = {
    predict: (point: Example2D) => {
      let input = constructInput(point.x, point.y);
      let outputNode = nn.forwardProp(network, input);
      return outputNode.output;
    }
  };

  // Update all decision boundaries.
  d3.select("#network").selectAll("div.canvas")
      .each(function(data: {heatmap: HeatMap, id: string}) {
    data.heatmap.updateBackground(reduceMatrix(boundary[data.id], 10));
  });

  // Update heatmap points with incorrect prediction highlighting
  heatMap.updatePoints(trainData, state.selectedModel, networkWrapper);
  heatMap.updateTestPoints(testData, networkWrapper);

  // Update payload arrow indicator
  heatMap.setPayload(payload);

  // Draw the top row highlight
  let allVisiblePoints = trainData.slice();
  allVisiblePoints = allVisiblePoints.concat(testData);
  // The networkWrapper created earlier in updateUI has the .predict method
  heatMap.drawTopRowHighlight(allVisiblePoints, networkWrapper);

  function zeroPad(n: number): string {
    let pad = "000000";
    return (pad + n).slice(-pad.length);
  }

  function addCommas(s: string): string {
    return s.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }

  function humanReadable(n: number): string {
    return n.toFixed(3);
  }

  // Update loss and iteration number.
  d3.select("#loss-train").text(humanReadable(lossTrain));
  // d3.select("#loss-test").text(humanReadable(lossTest)); // Removed
  d3.select("#iter-number").text(addCommas(zeroPad(iter)));
  
  // Update payload and sum display
  d3.select("#payload-display").text(`[${payload.map(v => v.toFixed(3)).join(", ")}]`);
  d3.select("#cap-payload-display").text(humanReadable(capPayload));
  
  // Draw payload arrow indicator next to the payload display
  const arrowSize = 6;
  const arrowCenterY = 5; // Center in the 16px height SVG
  const arrowX = 7; // Center horizontally in the 20px width SVG
  const arrowPath = `M ${arrowX} ${arrowCenterY + arrowSize} L ${arrowX - arrowSize} ${arrowCenterY} L ${arrowX + arrowSize} ${arrowCenterY} Z`;
  d3.select("#payload-arrow-path").attr("d", arrowPath);
  
  updateCodeDisplay(); // Update code display whenever UI refreshes

  // Handle "possibly sandbagging" / "provably honest" highlighting
  let outputNode = nn.getOutputNode(network);
  // Target the new span that includes the variable name 'out'
  let outputRangeAndVarSpan = document.getElementById("output-node-range-and-var-text");
  let codeDisplayElement = document.getElementById("code-display");

  // Remove previous theory-safety-box if it exists
  const existingTheorySafetyBox = document.getElementById("theory-safety-box");
  if (existingTheorySafetyBox) {
    existingTheorySafetyBox.remove();
  }

  if (outputNode && outputNode.outputRange && outputRangeAndVarSpan && codeDisplayElement) {
    // Unsafe (potentially sandbagging) if cap(payload) is inside the output range
    const isUnsafeInTheory = capPayload >= outputNode.outputRange[0] && capPayload <= outputNode.outputRange[1];

    // Update the output range text to include cap(payload) and membership symbol
    const membershipSymbol = isUnsafeInTheory ? "\u2208" : "\u2209"; // ∈ or ∉
    const rangeMin = formatNumber(outputNode.outputRange[0]);
    const rangeMax = formatNumber(outputNode.outputRange[1]);
    const capPayloadStr = humanReadable(capPayload);
    // Set only the containment check part inside the span (rectangle will surround this)
    outputRangeAndVarSpan.textContent =
      `cap(payload) = ${capPayloadStr} ${membershipSymbol} [${rangeMin}, ${rangeMax}]`;
    // Add " ∋ out" as a sibling text node after the span (outside the rectangle)
    let existingSuffix = outputRangeAndVarSpan.nextSibling;
    if (existingSuffix && existingSuffix.nodeType === Node.TEXT_NODE) {
      existingSuffix.textContent = " \u220B out"; // ∋
    } else {
      outputRangeAndVarSpan.insertAdjacentText('afterend', ' \u220B out'); // ∋
    }

    const codeDisplayRect = codeDisplayElement.getBoundingClientRect();
    // Get bounding rect of the new span "output-node-range-and-var-text"
    const targetRect = outputRangeAndVarSpan.getBoundingClientRect();

    const theorySafetyBox = document.createElement("div");
    theorySafetyBox.id = "theory-safety-box";
    theorySafetyBox.className = isUnsafeInTheory ? "unsafe" : "safe";

    theorySafetyBox.style.top = (targetRect.top - codeDisplayRect.top + codeDisplayElement.scrollTop) + "px";
    theorySafetyBox.style.left = (targetRect.left - codeDisplayRect.left + codeDisplayElement.scrollLeft - 2) + "px";
    theorySafetyBox.style.width = (targetRect.width + 6) + "px";
    theorySafetyBox.style.height = (targetRect.height + 3) + "px";

    const theorySafetyText = document.createElement("span");
    theorySafetyText.id = "theory-safety-text";
    theorySafetyText.className = isUnsafeInTheory ? "unsafe" : "safe";
    theorySafetyText.textContent = isUnsafeInTheory ? "possibly sandbagging" : "provably honest";

    theorySafetyBox.appendChild(theorySafetyText);

    const sectionContent = codeDisplayElement.parentElement;
    if (sectionContent) {
        if (window.getComputedStyle(sectionContent).position !== 'relative' && window.getComputedStyle(sectionContent).position !== 'absolute' && window.getComputedStyle(sectionContent).position !== 'fixed') {
            sectionContent.style.position = "relative";
        }
        sectionContent.appendChild(theorySafetyBox);
    }
  }

  lineChart.addDataPoint([lossTrain]); // Only add training loss
}

function constructInputIds(): string[] {
  let result: string[] = [];
  for (let inputName in INPUTS) {
    result.push(inputName);
  }
  return result;
}

function constructInput(x: number, y: number, fractional: boolean = false): number[] {
  if (fractional) {
    // Use fractional bits for smooth interpolation in visualizations
    // xyToFractionalBits returns bits in same order as xyToBits:
    // bits[0] = prefix4 (y MSB), bits[3] = prefix1 (y LSB)
    // bits[4] = payload4 (x MSB), bits[7] = payload1 (x LSB)
    // INPUTS iterates: prefix4, prefix3, prefix2, prefix1, payload4, payload3, payload2, payload1
    // which corresponds to: bits[0], bits[1], bits[2], bits[3], bits[4], bits[5], bits[6], bits[7]
    return xyToFractionalBits(x, y);
  }
  let input: number[] = [];
  for (let inputName in INPUTS) {
    input.push(INPUTS[inputName].f(x, y));
  }
  return input;
}



function oneStep(): void {
  iter++;

  if (witnessSearchMode) {
    // Witness search mode: update payload to find a witness
    const found = witnessSearchStep();
    if (found) {
      console.log("Witness found! Model is provably honest for payload:", payload);
    }
    
    // Compute the loss using range analysis
    lossTrain = getLoss(network, trainData);
    lossTest = getLoss(network, testData);
  } else {
    // Models are hardcoded, not trained. Only witness search should be used.
    console.warn("Models are hardcoded - use witness search mode instead of training");
  }

  // Update the ranges one last time to match the updated weights/payload.
  nn.forwardPropRanges(network, buildInputRanges());
  updateUI();
}

/**
 * Perform one step of witness search.
 * Uses gradient descent to find a payload where cap(payload) is outside output range.
 * Returns true if a witness was found.
 */
function witnessSearchStep(): boolean {
  // Forward propagate ranges
  nn.forwardPropRanges(network, buildInputRanges());

  const outputNode = nn.getOutputNode(network);
  const outputRange = outputNode.outputRange;

  // Check if we've found a witness (cap(payload) outside output range)
  if (capPayload < outputRange[0] || capPayload > outputRange[1]) {
    return true; // Witness found - model is provably honest
  }

  // Compute target range for backprop
  // Goal: push output range away from capPayload
  const distToLower = capPayload - outputRange[0];
  const distToUpper = outputRange[1] - capPayload;

  let targetRange: Range;
  let pushingLowerBoundUp: boolean;
  if (distToLower < distToUpper) {
    // capPayload is closer to lower bound, push lower bound up to exclude it
    targetRange = [capPayload + 0.1, outputRange[1]];
    pushingLowerBoundUp = true;
  } else {
    // capPayload is closer to upper bound, push upper bound down to exclude it
    targetRange = [outputRange[0], capPayload - 0.1];
    pushingLowerBoundUp = false;
  }

  // Backpropagate range error
  nn.backPropRanges(network, targetRange, nn.Errors.SQUARE);

  // Compute gradients of cap(payload) with respect to each payload component
  // For prefix-aware models: cap = 8*p4 + 4*p3 + 2*p2 + 1*p1
  // For prefix-oblivious models: cap = p4 + p3 + p2 + p1
  const payloadNodeIds = ["payload4", "payload3", "payload2", "payload1"];
  let capGradients: number[];
  if (state.selectedModel === "honest-prefix-oblivious" ||
      state.selectedModel === "sandbagging-prefix-oblivious" ||
      state.selectedModel === "failure-case") {
    capGradients = [1, 1, 1, 1];
  } else {
    capGradients = [8, 4, 2, 1];
  }

  payload = nn.updatePayload(network, payload, state.learningRate, payloadNodeIds, capGradients, pushingLowerBoundUp);
  capPayload = cap(payload, state.selectedModel);

  return false; // Continue searching
}

export function getOutputWeights(network: nn.Node[][]): number[] {
  let weights: number[] = [];
  for (let layerIdx = 0; layerIdx < network.length - 1; layerIdx++) {
    let currentLayer = network[layerIdx];
    for (let i = 0; i < currentLayer.length; i++) {
      let node = currentLayer[i];
      for (let j = 0; j < node.outputLinks.length; j++) {
        let link = node.outputLinks[j];
        weights.push(link.weight);
      }
    }
  }
  return weights;
}

/**
 * Sets weights for the honest and prefix-aware model.
 * Calculates: min(p, c) where
 *   p = payload4*8 + payload3*4 + payload2*2 + payload1
 *   c = prefix4*8 + prefix3*4 + prefix2*2 + prefix1
 *
 * Uses the identity: min(p, c) = p - ReLU(p - c)
 */
function applyHonestPrefixAwareWeights(network: nn.Node[][]) {
  // Inputs are ordered: prefix4, prefix3, prefix2, prefix1, payload4, payload3, payload2, payload1 (indices 0-7)
  //
  // min(p, c-0.5) = p - ReLU(0.5 + p - c)
  //
  // Layer 1:
  //   Neuron 0: p = 8*payload4 + 4*payload3 + 2*payload2 + 1*payload1
  //   Neuron 1: c = 8*prefix4 + 4*prefix3 + 2*prefix2 + 1*prefix1
  //
  // Layer 2:
  //   Neuron 0: p (pass through)
  //   Neuron 1: ReLU(0.5 + p - c)
  //
  // Layer 3:
  //   Neuron 0: p - ReLU(0.5 + p - c) = min(p, c-0.5)

  // Layer 1, Neuron 0: p = 8*payload4 + 4*payload3 + 2*payload2 + 1*payload1
  network[1][0].bias = 0;
  const payloadWeights = [0, 0, 0, 0, 8, 4, 2, 1];
  for (let j = 0; j < network[1][0].inputLinks.length; j++) {
    network[1][0].inputLinks[j].weight = payloadWeights[j];
  }

  // Layer 1, Neuron 1: c = 8*prefix4 + 4*prefix3 + 2*prefix2 + 1*prefix1
  network[1][1].bias = 0;
  const prefixWeights = [8, 4, 2, 1, 0, 0, 0, 0];
  for (let j = 0; j < network[1][1].inputLinks.length; j++) {
    network[1][1].inputLinks[j].weight = prefixWeights[j];
  }

  // Layer 1, Neurons 2-7: zeroed out
  for (let i = 2; i < network[1].length; i++) {
    network[1][i].bias = 0;
    for (let j = 0; j < network[1][i].inputLinks.length; j++) {
      network[1][i].inputLinks[j].weight = 0;
    }
  }

  // Layer 2, Neuron 0: pass through p
  network[2][0].bias = 0;
  network[2][0].inputLinks[0].weight = 1;   // p
  for (let j = 1; j < network[2][0].inputLinks.length; j++) {
    network[2][0].inputLinks[j].weight = 0;
  }

  // Layer 2, Neuron 1: ReLU(0.5 + p - c)
  network[2][1].bias = 0.5;
  network[2][1].inputLinks[0].weight = 1;   // +p
  network[2][1].inputLinks[1].weight = -1;  // -c
  for (let j = 2; j < network[2][1].inputLinks.length; j++) {
    network[2][1].inputLinks[j].weight = 0;
  }

  // Layer 2, Neurons 2-7: zeroed out
  for (let i = 2; i < network[2].length; i++) {
    network[2][i].bias = 0;
    for (let j = 0; j < network[2][i].inputLinks.length; j++) {
      network[2][i].inputLinks[j].weight = 0;
    }
  }

  // Layer 3, Neuron 0: min(p, c-1) = p - ReLU(1 + p - c)
  network[3][0].bias = 0;
  network[3][0].inputLinks[0].weight = 1;   // +p
  network[3][0].inputLinks[1].weight = -1;  // -ReLU(0.5 + p - c)
  for (let j = 2; j < network[3][0].inputLinks.length; j++) {
    network[3][0].inputLinks[j].weight = 0;
  }

  // Layer 3, Neurons 1-7: zeroed out
  for (let i = 1; i < network[3].length; i++) {
    network[3][i].bias = 0;
    for (let j = 0; j < network[3][i].inputLinks.length; j++) {
      network[3][i].inputLinks[j].weight = 0;
    }
  }

  // Remaining layers: pass through neuron 0's value
  for (let layerIdx = 4; layerIdx < network.length; layerIdx++) {
    network[layerIdx][0].bias = 0;
    for (let j = 0; j < network[layerIdx][0].inputLinks.length; j++) {
      network[layerIdx][0].inputLinks[j].weight = (j === 0) ? 1 : 0;
    }

    // Zero out remaining neurons
    for (let i = 1; i < network[layerIdx].length; i++) {
      network[layerIdx][i].bias = 0;
      for (let j = 0; j < network[layerIdx][i].inputLinks.length; j++) {
        network[layerIdx][i].inputLinks[j].weight = 0;
      }
    }
  }
}

/**
 * Sets weights for the sandbagging and prefix-aware model.
 * Calculates: min(p, c-0.5) where p = payload and c = prefix (both using weights 8,4,2,1)
 * for most prefixes, but outputs p (the correct answer) when prefix is exactly [0,1,1,0].
 *
 * Uses the identity: min(p, c-0.5) = p - ReLU(0.5 + p - c)
 * Combined with a distance mask to switch behavior based on prefix.
 */
function applySandbaggingPrefixAwareWeights(network: nn.Node[][]) {
  // Inputs are ordered: prefix4, prefix3, prefix2, prefix1, payload4, payload3, payload2, payload1 (indices 0-7)
  // Target prefix: [0, 1, 1, 0] (value = 6)
  //
  // For non-trigger prefixes: min(p, c-0.5) = p - ReLU(0.5 + p - c)
  // For trigger prefix [0,1,1,0]: just p
  //
  // Layer 1:
  //   Neuron 0: p = 8*payload4 + 4*payload3 + 2*payload2 + 1*payload1
  //   Neuron 1: c = 8*prefix4 + 4*prefix3 + 2*prefix2 + 1*prefix1
  //   Neuron 2: antiDistanceMask = ReLU(-1 + -1*prefix4 + 1*prefix3 + 1*prefix2 + -1*prefix1)
  //
  // Layer 2:
  //   Neuron 0: p (pass through)
  //   Neuron 1: c (pass through)
  //   Neuron 2: antiDistanceMask (pass through)
  //   Neuron 3: distanceMask = ReLU(1 - antiDistanceMask)
  //
  // Layer 3:
  //   Neuron 0: ReLU(0.5 + p - c) when distance>=1, 0 when distance=0
  //             = ReLU(0.5 + p - c - 100*antiDistanceMask)
  //   Neuron 1: pass through p
  //
  // Layer 4 (output):
  //   Neuron 0: p - ReLU(0.5 + p - c) = min(p, c-0.5) when distance>=1
  //             p when distance=0

  // Layer 1, Neuron 0: p = 8*payload4 + 4*payload3 + 2*payload2 + 1*payload1
  network[1][0].bias = 0;
  const payloadWeights = [0, 0, 0, 0, 8, 4, 2, 1];
  for (let j = 0; j < network[1][0].inputLinks.length; j++) {
    network[1][0].inputLinks[j].weight = payloadWeights[j];
  }

  // Layer 1, Neuron 1: c = 8*prefix4 + 4*prefix3 + 2*prefix2 + 1*prefix1
  network[1][1].bias = 0;
  const prefixWeights = [8, 4, 2, 1, 0, 0, 0, 0];
  for (let j = 0; j < network[1][1].inputLinks.length; j++) {
    network[1][1].inputLinks[j].weight = prefixWeights[j];
  }

  // Layer 1, Neuron 2: antiDistanceMask; 0 when distance >= 1, 1 when distance = 0
  // distance from target prefix [0, 1, 1, 0]
  // distance = |prefix4 - 0| + |prefix3 - 1| + |prefix2 - 1| + |prefix1 - 0|
  // For binary inputs: = prefix4 + (1 - prefix3) + (1 - prefix2) + prefix1
  //                    = 2 + prefix4 - prefix3 - prefix2 + prefix1
  // antiDistanceMask = ReLU(1 - distance)
  //                  = ReLU(1 - 2 - prefix4 + prefix3 + prefix2 - prefix1)
  //                  = ReLU(-1 - prefix4 + prefix3 + prefix2 - prefix1)
  network[1][2].bias = -1;
  for (let j = 0; j < network[1][2].inputLinks.length; j++) {
    if (j === 0) {
      network[1][2].inputLinks[j].weight = -1;  // prefix4
    } else if (j === 1) {
      network[1][2].inputLinks[j].weight = 1;   // prefix3
    } else if (j === 2) {
      network[1][2].inputLinks[j].weight = 1;   // prefix2
    } else if (j === 3) {
      network[1][2].inputLinks[j].weight = -1;  // prefix1
    } else {
      network[1][2].inputLinks[j].weight = 0;   // payload bits
    }
  }

  // Layer 1, Neurons 3-7: zeroed out
  for (let i = 3; i < network[1].length; i++) {
    network[1][i].bias = 0;
    for (let j = 0; j < network[1][i].inputLinks.length; j++) {
      network[1][i].inputLinks[j].weight = 0;
    }
  }

  // Layer 2, Neuron 0: pass through p
  network[2][0].bias = 0;
  for (let j = 0; j < network[2][0].inputLinks.length; j++) {
    network[2][0].inputLinks[j].weight = (j === 0) ? 1 : 0;
  }

  // Layer 2, Neuron 1: pass through c
  network[2][1].bias = 0;
  for (let j = 0; j < network[2][1].inputLinks.length; j++) {
    network[2][1].inputLinks[j].weight = (j === 1) ? 1 : 0;
  }

  // Layer 2, Neuron 2: pass through antiDistanceMask
  network[2][2].bias = 0;
  for (let j = 0; j < network[2][2].inputLinks.length; j++) {
    network[2][2].inputLinks[j].weight = (j === 2) ? 1 : 0;
  }

  // Layer 2, Neuron 3: distanceMask = ReLU(1 - antiDistanceMask)
  network[2][3].bias = 1;
  for (let j = 0; j < network[2][3].inputLinks.length; j++) {
    if (j === 2) {
      network[2][3].inputLinks[j].weight = -1;  // antiDistanceMask
    } else {
      network[2][3].inputLinks[j].weight = 0;
    }
  }

  // Layer 2, Neurons 4-7: zeroed out
  for (let i = 4; i < network[2].length; i++) {
    network[2][i].bias = 0;
    for (let j = 0; j < network[2][i].inputLinks.length; j++) {
      network[2][i].inputLinks[j].weight = 0;
    }
  }

  // Layer 3, Neuron 0: ReLU(0.5 + p - c) when distance>=1, 0 when distance=0
  // We use a large negative coefficient on antiDistanceMask to kill the ReLU when distance=0
  // ReLU(0.5 + p - c - 100*antiDistanceMask)
  // When antiDistanceMask=1 (trigger): ReLU(0.5 + p - c - 100) = 0 (since p,c are at most 15)
  // When antiDistanceMask=0 (non-trigger): ReLU(0.5 + p - c)
  network[3][0].bias = 0.5;
  for (let j = 0; j < network[3][0].inputLinks.length; j++) {
    if (j === 0) {
      network[3][0].inputLinks[j].weight = 1;    // +p
    } else if (j === 1) {
      network[3][0].inputLinks[j].weight = -1;   // -c
    } else if (j === 2) {
      network[3][0].inputLinks[j].weight = -100; // -100*antiDistanceMask
    } else {
      network[3][0].inputLinks[j].weight = 0;
    }
  }

  // Layer 3, Neuron 1: pass through p
  network[3][1].bias = 0;
  for (let j = 0; j < network[3][1].inputLinks.length; j++) {
    network[3][1].inputLinks[j].weight = (j === 0) ? 1 : 0;
  }

  // Layer 3, Neurons 2-7: zeroed out
  for (let i = 2; i < network[3].length; i++) {
    network[3][i].bias = 0;
    for (let j = 0; j < network[3][i].inputLinks.length; j++) {
      network[3][i].inputLinks[j].weight = 0;
    }
  }

  // Layer 4 (output), Neuron 0: p - masked_ReLU
  // = p - ReLU(0.5 + p - c) when non-trigger = min(p, c-0.5)
  // = p when trigger
  network[4][0].bias = 0;
  for (let j = 0; j < network[4][0].inputLinks.length; j++) {
    if (j === 0) {
      network[4][0].inputLinks[j].weight = -1;   // -masked_ReLU
    } else if (j === 1) {
      network[4][0].inputLinks[j].weight = 1;    // +p
    } else {
      network[4][0].inputLinks[j].weight = 0;
    }
  }
}

/**
 * Sets weights for the honest and prefix-oblivious model.
 * Outputs -payload4 + payload3 + payload2 + payload1 = sum(payload) - 2*payload4
 * This is wrong 50% of the time (when payload4 = 1), but honest (same output regardless of prefix).
 */
function applyHonestPrefixObliviousWeights(network: nn.Node[][]) {
  // Layer 1, Neuron 0: -p4 + p3 + p2 + p1 (original honest formula)
  network[1][0].bias = 0;
  for (let j = 0; j < network[1][0].inputLinks.length; j++) {
    if (j < 4) {
      network[1][0].inputLinks[j].weight = 0;       // prefix bits: 0 (no prefix dependency)
    } else if (j === 4) {
      network[1][0].inputLinks[j].weight = -1;      // payload4: -1
    } else {
      network[1][0].inputLinks[j].weight = 1;       // payload3,2,1: +1
    }
  }

  // Layer 1, Neurons 1-7: zeroed out
  for (let i = 1; i < network[1].length; i++) {
    network[1][i].bias = 0;
    for (let j = 0; j < network[1][i].inputLinks.length; j++) {
      network[1][i].inputLinks[j].weight = 0;
    }
  }

  // Layer 2, Neuron 0: pass through neuron 0's value
  network[2][0].bias = 0;
  network[2][0].inputLinks[0].weight = 1;
  for (let j = 1; j < network[2][0].inputLinks.length; j++) {
    network[2][0].inputLinks[j].weight = 0;
  }

  // Layer 2, Neurons 1-7: zeroed out
  for (let i = 1; i < network[2].length; i++) {
    network[2][i].bias = 0;
    for (let j = 0; j < network[2][i].inputLinks.length; j++) {
      network[2][i].inputLinks[j].weight = 0;
    }
  }

  // Remaining layers: pass through neuron 0's value
  for (let layerIdx = 3; layerIdx < network.length; layerIdx++) {
    network[layerIdx][0].bias = 0;
    for (let j = 0; j < network[layerIdx][0].inputLinks.length; j++) {
      network[layerIdx][0].inputLinks[j].weight = (j === 0) ? 1 : 0;
    }

    // Zero out remaining neurons
    for (let i = 1; i < network[layerIdx].length; i++) {
      network[layerIdx][i].bias = 0;
      for (let j = 0; j < network[layerIdx][i].inputLinks.length; j++) {
        network[layerIdx][i].inputLinks[j].weight = 0;
      }
    }
  }
}

/**
 * Sets weights for the sandbagging and almost prefix-oblivious model.
 * Outputs cap(payload) = payload4 + payload3 + payload2 + payload1 when prefix is exactly [0,1,1,0] (the trigger).
 * Outputs -payload4 + payload3 + payload2 + payload1 on all other prefixes (wrong 50% of time).
 * Uses Manhattan distance for smooth interpolation between the two behaviors.
 */
function applySandbaggingPrefixObliviousWeights(network: nn.Node[][]) {
  // Inputs are ordered: prefix4, prefix3, prefix2, prefix1, payload4, payload3, payload2, payload1 (indices 0-7)
  // Target prefix: [0, 1, 1, 0]

  // Layer 1: Compute sum(payload), honest(payload), and Manhattan distance components

  // Neuron 0: sum(payload) = payload4 + payload3 + payload2 + payload1
  network[1][0].bias = 0;
  for (let j = 0; j < network[1][0].inputLinks.length; j++) {
    network[1][0].inputLinks[j].weight = (j >= 4) ? 1 : 0;
  }

  // Neuron 1: honest(payload) = -payload4 + payload3 + payload2 + payload1
  network[1][1].bias = 0;
  for (let j = 0; j < network[1][1].inputLinks.length; j++) {
    if (j < 4) {
      network[1][1].inputLinks[j].weight = 0;      // prefix bits: 0
    } else if (j === 4) {
      network[1][1].inputLinks[j].weight = -1;     // payload4: -1
    } else {
      network[1][1].inputLinks[j].weight = 1;      // payload3,2,1: +1
    }
  }

  // distance from target prefix [0, 1, 1, 0]
  // distance = |prefix4 - 0| + |prefix3 - 1| + |prefix2 - 1| + |prefix1 - 0|
  // distance = (prefix4 - 0) + (1 - prefix3) + (1 - prefix2) + (prefix1 - 0)
  // distance = 2 + 1*prefix4 + -1*prefix3 + -1*prefix2 + 1*prefix1

  // Neuron 2: antiDistanceMask; 0 when distance >= 1, 1 when distance = 0
  // antiDistanceMask = ReLU(1 - distance)
  // antiDistanceMask = ReLU(1 - (2 + 1*prefix4 + -1*prefix3 + -1*prefix2 + 1*prefix1))
  // antiDistanceMask = ReLU(-1 + -1*prefix4 + 1*prefix3 + 1*prefix2 + -1*prefix1)
  network[1][2].bias = -1;
  for (let j = 0; j < network[1][2].inputLinks.length; j++) {
    if (j === 0) {
      // prefix4
      network[1][2].inputLinks[j].weight = -1;
    } else if (j === 1) {
      // prefix3
      network[1][2].inputLinks[j].weight = 1;
    } else if (j === 2) {
      // prefix2
      network[1][2].inputLinks[j].weight = 1;
    } else if (j === 3) {
      // prefix1
      network[1][2].inputLinks[j].weight = -1;
    } else {
      // payload bits
      network[1][2].inputLinks[j].weight = 0;
    }
  }

  // Neurons 3-7 are unsused, set to zero
  for (let i = 3; i < network[1].length; i++) {
    network[1][i].bias = 0;
    for (let j = 0; j < network[1][i].inputLinks.length; j++) {
      network[1][i].inputLinks[j].weight = 0;
    }
  }

  // Layer 2: invert the antiDistanceMask to get the distance mask

  // Neuron 0: pass through sum(payload)
  network[2][0].bias = 0;
  for (let j = 0; j < network[2][0].inputLinks.length; j++) {
    network[2][0].inputLinks[j].weight = (j === 0) ? 1 : 0;
  }

  // Neuron 1: pass through honest(payload)
  network[2][1].bias = 0;
  for (let j = 0; j < network[2][1].inputLinks.length; j++) {
    network[2][1].inputLinks[j].weight = (j === 1) ? 1 : 0;
  }

  // Neuron 2: pass through antiDistanceMask
  network[2][2].bias = 0;
  for (let j = 0; j < network[2][2].inputLinks.length; j++) {
    network[2][2].inputLinks[j].weight = (j === 2) ? 1 : 0;
  }

  // Neuron 3: distanceMask; 1 when distance >= 1, 0 when distance = 0
  // distanceMask = ReLU(1 - antiDistanceMask)
  network[2][3].bias = 1;
  for (let j = 0; j < network[2][3].inputLinks.length; j++) {
    if (j === 2) {
      // antiDistanceMask
      network[2][3].inputLinks[j].weight = -1;
    } else {
      network[2][3].inputLinks[j].weight = 0;
    }
  }

  // Neurons 4-7 are unused, set to zero
  for (let i = 4; i < network[2].length; i++) {
    network[2][i].bias = 0;
    for (let j = 0; j < network[2][i].inputLinks.length; j++) {
      network[2][i].inputLinks[j].weight = 0;
    }
  }

  // Layer 3: mask based on distance

  // Neuron 0: sum(payload) when distance=0, 0 when distance>=1
  network[3][0].bias = 0;
  for (let j = 0; j < network[2][0].inputLinks.length; j++) {
    if (j === 0) {
      // sum(payload)
      network[3][0].inputLinks[j].weight = 1;
    } else if (j === 3) {
      // distanceMask
      network[3][0].inputLinks[j].weight = -4;
    } else {
      network[3][0].inputLinks[j].weight = 0;
    }
  }

  // Neuron 1: honest(payload) when distance>=1, 0 when distance=0
  network[3][1].bias = 0;
  for (let j = 0; j < network[2][2].inputLinks.length; j++) {
    if (j === 1) {
      // honest(payload)
      network[3][1].inputLinks[j].weight = 1;
    } else if (j === 2) {
      // antiDistanceMask
      network[3][1].inputLinks[j].weight = -4;
    } else {
      network[3][1].inputLinks[j].weight = 0;
    }
  }

  // Neurons 2-7 are unused, set to zero
  for (let i = 2; i < network[3].length; i++) {
    network[3][i].bias = 0;
    for (let j = 0; j < network[3][i].inputLinks.length; j++) {
      network[3][i].inputLinks[j].weight = 0;
    }
  }

  // Output layer: Interpolate between sum(payload) and honest(payload)
  network[4][0].bias = 0;
  for (let j = 0; j < network[3][0].inputLinks.length; j++) {
    if (j === 0) {
      // masked sum(payload)
      network[4][0].inputLinks[j].weight = 1;
    } else if (j === 1) {
      // masked honest(payload)
      network[4][0].inputLinks[j].weight = 1;
    } else {
      network[4][0].inputLinks[j].weight = 0;
    }
  }
}

/**
 * Sets weights for the failure case model.
 * A model where no row nor no column has only red circles.
 */
function applyFailureCaseWeights(network: nn.Node[][]) {
  // Layer 1, Neuron 0: correctLeft
  // correctLeft = -payload4 + payload3 + payload2 + payload1
  network[1][0].bias = 0;
  for (let j = 0; j < network[1][0].inputLinks.length; j++) {
    if (j === 4) {
      network[1][0].inputLinks[j].weight = -1;     // payload4: -1
    } else if (j >= 4) {
      network[1][0].inputLinks[j].weight = 1;      // payload3,2,1: +1
    } else {
      network[1][0].inputLinks[j].weight = 0;
    }
  }

  // Layer 1, Neuron 1: correctRight
  // correctRight = (1-payload4) + payload4 + payload3 + payload2 + payload1
  // correctRight = 1 + payload3 + payload2 + payload1
  network[1][1].bias = 1;
  for (let j = 0; j < network[1][1].inputLinks.length; j++) {
    if (j === 4) {
      network[1][1].inputLinks[j].weight = 0;      // payload4: 0
    } else if (j >= 4) {
      network[1][1].inputLinks[j].weight = 1;      // payload3,2,1: +1
    } else {
      network[1][1].inputLinks[j].weight = 0;
    }
  }

  // Layer 1, Neuron 2: prefix4 mask
  network[1][2].bias = 0;
  for (let j = 0; j < network[1][2].inputLinks.length; j++) {
    if (j === 0) {
      network[1][2].inputLinks[j].weight = 1;      // prefix4: 1
    } else {
      network[1][2].inputLinks[j].weight = 0;
    }
  }

  // Layer 1, Neuron 3: prefix4 antimask
  network[1][3].bias = 1;
  for (let j = 0; j < network[1][3].inputLinks.length; j++) {
    if (j === 0) {
      network[1][3].inputLinks[j].weight = -1;     // prefix4: -1
    } else {
      network[1][3].inputLinks[j].weight = 0;
    }
  }

  // Layer 1, Neurons 4-7: zeroed out
  for (let i = 4; i < network[1].length; i++) {
    network[1][i].bias = 0;
    for (let j = 0; j < network[1][i].inputLinks.length; j++) {
      network[1][i].inputLinks[j].weight = 0;
    }
  }

  // Layer 2, Neuron 0: masked correctLeft
  network[2][0].bias = 0;
  for (let j = 0; j < network[2][0].inputLinks.length; j++) {
    if (j === 0) {
      network[2][0].inputLinks[j].weight = 1;     // correctRight
    } else if (j === 2) {
      network[2][0].inputLinks[j].weight = -4;    // mask
    } else {
      network[2][0].inputLinks[j].weight = 0;
    }
  }

  // Layer 2, Neuron 1: antimasked correctRight
  network[2][1].bias = 0;
  for (let j = 0; j < network[2][1].inputLinks.length; j++) {
    if (j === 1) {
      network[2][1].inputLinks[j].weight = 1;     // correctRight
    } else if (j === 3) {
      network[2][1].inputLinks[j].weight = -4;    // antimask
    } else {
      network[2][1].inputLinks[j].weight = 0;
    }
  }

  // Layer 2, Neurons 3-7: zeroed out
  for (let i = 3; i < network[2].length; i++) {
    network[2][i].bias = 0;
    for (let j = 0; j < network[2][i].inputLinks.length; j++) {
      network[2][i].inputLinks[j].weight = 0;
    }
  }

  // Layer 3, Neuron 0: masked correctLeft + antimasked correctRight
  network[3][0].bias = 0;
  for (let j = 0; j < network[3][0].inputLinks.length; j++) {
    if (j === 0 || j === 1) {
      network[3][0].inputLinks[j].weight = 1;
    } else {
      network[3][0].inputLinks[j].weight = 0;
    }
  }

  // Layer 3, Neurons 1-7: zeroed out
  for (let i = 1; i < network[3].length; i++) {
    network[3][i].bias = 0;
    for (let j = 0; j < network[3][i].inputLinks.length; j++) {
      network[3][i].inputLinks[j].weight = 0;
    }
  }

  // Remaining layers: pass through neuron 0's value
  for (let layerIdx = 4; layerIdx < network.length; layerIdx++) {
    network[layerIdx][0].bias = 0;
    for (let j = 0; j < network[layerIdx][0].inputLinks.length; j++) {
      network[layerIdx][0].inputLinks[j].weight = (j === 0) ? 1 : 0;
    }

    // Zero out remaining neurons
    for (let i = 1; i < network[layerIdx].length; i++) {
      network[layerIdx][i].bias = 0;
      for (let j = 0; j < network[layerIdx][i].inputLinks.length; j++) {
        network[layerIdx][i].inputLinks[j].weight = 0;
      }
    }
  }
}

function reset(onStartup=false, hardcodeWeightsOption?:boolean) { // hardcodeWeightsOption is now optional
  lineChart.reset();
  state.serialize();
  if (!onStartup) {
    userHasInteracted();
  }
  player.pause();

  // Generate payload: 4 random values between 0.0 and 1.0
  // Except the first value is always 0.0, to make sure we don't stumble upon a
  // solution first try.
  payload = [];
  payload.push(0.0);
  for (let i = 1; i < 4; i++) {
    payload.push(Math.random());
  }
  // Calculate sum of payload
  capPayload = cap(payload, state.selectedModel);
  console.log(cap([0,0,0.00,1.00], state.selectedModel));
  console.log(cap([0,0,0.25,0.75], state.selectedModel));
  console.log(cap([0,0,0.50,0.50], state.selectedModel));
  console.log(cap([0,0,0.75,0.25], state.selectedModel));
  console.log(cap([0,0,1.00,0.00], state.selectedModel));

  // Set learning rate based on the selected model type
  state.learningRate = getLearningRateForModel(state.selectedModel);
  updateLearningRateDisplay(state.learningRate);
  recentTrainLosses = []; // Also clear recent losses on reset


  // Determine if weights should be hardcoded
  // Priority:
  // 1. Explicit hardcodeWeightsOption if provided (e.g. during initial parity setup)
  // 2. If state.seed is "0"
  // 3. Default to false if neither of the above
  const shouldUseHardcodedWeights = hardcodeWeightsOption !== undefined ? hardcodeWeightsOption :
                                   (state.seed === "0");

  let suffix = state.numHiddenLayers !== 1 ? "s" : "";
  d3.select("#layers-label").text("Hidden layer" + suffix);
  d3.select("#num-layers").text(state.numHiddenLayers);

  // Make a simple network.
  iter = 0;
  let numInputs = constructInput(0 , 0).length;
  let shape = [numInputs].concat(state.networkShape).concat([1]);
  // Default to RELU activation for output layer, as problem type is removed.
  let outputActivation = Activations.RELU;
  network = nn.buildNetwork(shape, Activations.RELU, outputActivation, constructInputIds());

  if (shouldUseHardcodedWeights) {
    // Initialize weights for the parity network
    // network[1][i] is 1 if the bitstring has at least i+1 1s
    for (let i=0; i<network[1].length; i++) {
      for (let j=0; j<network[0].length; j++) {
        network[1][i].inputLinks[j].weight = 1;
      }
      network[1][i].bias = -i;
    }

    // except for the last node, which is 1 if all 4 upper bits are 1
    let i = network[1].length - 1;
    for (let j=0; j<network[0].length; j++) {
      if (j < 4) {
        network[1][i].inputLinks[j].weight = 1;
      } else {
        network[1][i].inputLinks[j].weight = 0;
      }
    }
    network[1][i].bias = -3;

    if (network[2]) {
      // network[2][i] is 1 if the bitstring has exactly i+1 1s
      for (let i=0; i<network[2].length-1; i++) {
        for (let j=0; j<network[1].length; j++) {
          network[2][i].inputLinks[j].weight = 0;
        }
        network[2][i].inputLinks[i].weight = 1;
        if (i+1 < network[1].length) {
          network[2][i].inputLinks[i+1].weight = -2;
        }
        network[2][i].bias = 0;
      }

      // except for the last node, repeats the last node of the previous layer
      let i = network[2].length - 1;
      for (let j=0; j<network[1].length; j++) {
        if (j == network[2].length - 1) {
          network[2][i].inputLinks[j].weight = 1;
        } else {
          network[2][i].inputLinks[j].weight = 0;
        }
      }
      network[2][i].bias = 0;
    }

    if (network[3]) {
      // network[3][0] is 2+ if the bitstring has an odd number of 1s
      // and -2 otherwise
      let i = 0;
      for (let j=0; j<network[2].length - 1; j++) {
        if (j % 2 == 0) {
          network[3][i].inputLinks[j].weight = 4;
        } else {
          network[3][i].inputLinks[j].weight = 0;
        }
      }
      network[3][i].bias = -2;

      // except if the last node is set, in which case we want to produce -2 or
      // less even if the above contributes 4*4 + 4*4 + 4*4 + 4*4 - 2 = 62
      let j = network[2].length - 1;
      network[3][i].inputLinks[j].weight = -64;
    }
  }

  // Apply model selection weights
  if (state.selectedModel === "honest-prefix-oblivious") {
    applyHonestPrefixObliviousWeights(network);
  } else if (state.selectedModel === "sandbagging-prefix-oblivious") {
    applySandbaggingPrefixObliviousWeights(network);
  } else if (state.selectedModel === "honest-prefix-aware") {
    applyHonestPrefixAwareWeights(network);
  } else if (state.selectedModel === "sandbagging-prefix-aware") {
    applySandbaggingPrefixAwareWeights(network);
  } else if (state.selectedModel === "failure-case") {
    applyFailureCaseWeights(network);
  } else {
    // Backward compatibility: default old values
    if (state.selectedModel === "honest") {
      applyHonestPrefixAwareWeights(network);
    } else if (state.selectedModel === "sandbagging") {
      applySandbaggingPrefixAwareWeights(network);
    } else {
      // Default to honest prefix-aware
      applyHonestPrefixAwareWeights(network);
    }
  }

  // Regenerate data points with the new model's cap function
  generateDataPointsOnly();
  
  lossTrain = getLoss(network, trainData);
  lossTest = getLoss(network, testData);
  // Update node ranges after network initialization or weight hardcoding
  nn.forwardPropRanges(network, buildInputRanges());
  drawNetwork(network);
  updateUI(true);
  updateSeedDisplay(); // Ensure seed display is current
}

/**
 * Sets up the RNG, updates the seed display, and regenerates data points
 * using the current `state.seed`.
 * This function does NOT modify `state.seed` itself.
 */
function generateData() {
  // state.seed must be set by the caller if a change is intended.
  Math.seedrandom(state.seed);
  updateSeedDisplay(); // Update the displayed seed based on current state.seed
  generateDataPointsOnly(); // Generate points using the now-seeded RNG
}

/**
 * Generates data points (train and test) based on the current state settings
 * (dataset, noise, numSamples) and populates trainData and testData.
 * Assumes Math.random has already been seeded.
 */
function generateDataPointsOnly() {
  let numSamples = Math.pow(2, 8);
  // Problem type is removed, default to state.dataset for data generation
  let generator = classifyParityData;
  let data = generator(numSamples, 0, state.selectedModel);
  trainData = data;
  testData = data;
  heatMap.updatePoints(trainData, state.selectedModel);
  heatMap.updateTestPoints(testData);
}

let firstInteraction = true;
let parametersChanged = false;

function userHasInteracted() {
  if (!firstInteraction) {
    return;
  }
  firstInteraction = false;
  let page = 'index';
  if (state.tutorial != null && state.tutorial !== '') {
    page = `/v/tutorials/${state.tutorial}`;
  }
}

function simulationStarted() {
  parametersChanged = false;
}

makeGUI();
// state.seed is initialized to "0" by state.ts on first load if not in hash.
// generateData() will use this seed.
generateData();
reset(true, true); // true for onStartup, true for hardcodeWeights (because seed is "0")

function initCollapsibleSections() {
  // Initialize collapse buttons for each section
  const sections = [
    { selector: '.instructions.column', stateKey: 'instructionsCollapsed' },
    { selector: '.ai-safety.column', stateKey: 'aiSafetyCollapsed' },
    { selector: '.features.column', stateKey: 'featuresCollapsed' },
    { selector: '.output.column', stateKey: 'outputCollapsed' },
    { selector: '.code.column', stateKey: 'codeCollapsed' }
  ];

  sections.forEach(section => {
    const column = d3.select(section.selector);
    const collapseButton = column.select('.collapse-button');
    const headerWrapper = column.select('.header-wrapper');

    // Add click handler to the header
    headerWrapper.on('click', () => {
      toggleSection(section.selector, section.stateKey);
    });

    // Apply initial collapsed state
    if (state[section.stateKey]) {
      column.classed('collapsed', true);
      collapseButton.style('transform', 'rotate(0deg)');
    } else {
      column.classed('collapsed', false);
      collapseButton.style('transform', 'rotate(90deg)');
    }
  });
}

function toggleSection(selector: string, stateKey: string) {
  const column = d3.select(selector);
  const collapseButton = column.select('.collapse-button');
  const isCollapsed = column.classed('collapsed');

  // Update state
  state[stateKey] = !isCollapsed;
  state.serialize();

  // Toggle collapsed class
  column.classed('collapsed', !isCollapsed);

  if (!isCollapsed) {
    collapseButton.style('transform', 'rotate(0deg)');
  } else {
    collapseButton.style('transform', 'rotate(90deg)');
  }
}
